import { useEffect, useState } from 'react';
import Box from 'elements/Box';
import {
  Button,
  Chip,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useSnackbar } from 'notistack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useNavigate } from 'react-router-dom';

import { ApiRequest } from 'GlobalFunctions';
import AlertDialog from 'elements/AlertDialog';

export default function PurchaseOrdersList(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [openAlert, setOpenAlert] = useState(false);
  const [activeItem, setActiveItem] = useState(0);

  // Filtros
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadList();
  }, [page, search, statusFilter]);

  const loadList = async () => {
    setLoading(true);

    await ApiRequest({
      url: "/purchaseorders/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        page: page,
        page_size: 20,
        search: search || undefined,
        status: statusFilter || undefined,
      },
      setResponse: (response: any) => {
        setList(response.list);
        setTotalPages(response.total_pages);
        setLoading(false);
      },
    });
  };

  const handleDelete = (purchase_order_id: number) => {
    setActiveItem(purchase_order_id);
    setOpenAlert(true);
  };

  const deleteItem = async () => {
    setLoading(true);

    await ApiRequest({
      url: "/purchaseorders/delete",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: {
        purchase_order_id: activeItem
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar('Orden de compra eliminada con éxito', { variant: 'success' });
          loadList();
        } else {
          enqueueSnackbar(response.message || 'Error al eliminar', { variant: 'error' });
        }
        setOpenAlert(false);
        setLoading(false);
      },
    });
  };

  const exportExcel = async (purchase_order_id: number) => {
    await ApiRequest({
      url: `/purchaseorders/report/${purchase_order_id}/excel`,
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          window.open(response.fileUrl, '_blank');
        } else {
          enqueueSnackbar('Error al generar reporte', { variant: 'error' });
        }
      },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_process':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'in_process':
        return 'En Proceso';
      case 'completed':
        return 'Completado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const getReceivingStatusColor = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'default';
      case 'partial':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getReceivingStatusLabel = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'No iniciado';
      case 'partial':
        return 'Parcial';
      case 'completed':
        return 'Completado';
      default:
        return status;
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return '$0';

    const isInteger = amount % 1 === 0;

    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: isInteger ? 0 : 2,
      maximumFractionDigits: isInteger ? 0 : 2
    }).format(amount).replace('COP', '').trim();
  };

  return (
    <div id='PurchaseOrdersList' className='ModuleContainer'>
      <div className='ModuleWrapper'>
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Órdenes de Compra</h2>
            <span className="h2_subtitle">Facturas XML DIAN importadas</span>
          </div>
        </div>

        <Box id="PurchaseOrdersListIn" className="box100">
          {/* Filtros */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <TextField
              label="Buscar por factura o proveedor"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              variant="outlined"
              size="small"
              style={{ minWidth: '150px' }}
            >
              <option value="">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="in_process">En Proceso</option>
              <option value="completed">Completado</option>
              <option value="cancelled">Cancelado</option>
            </TextField>
          </div>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>N° Factura</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Bodega</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Recepción</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((item: any, i) => (
                  <TableRow key={i} hover role="checkbox">
                    <TableCell>
                      <strong>{item.invoice_number}</strong>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className='flex_title'>{item.supplier_name}</span>
                        <span className='flex_subtitle'>NIT: {item.supplier_doc}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(item.issue_date).toLocaleDateString('es-CO')}
                    </TableCell>
                    <TableCell>
                      {item.warehouse_name || <em style={{ color: '#999' }}>Sin asignar</em>}
                    </TableCell>
                    <TableCell align="right">
                      <strong>{formatCurrency(item.total_amount)}</strong>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${item.items_received || 0}/${item.items_count || 0}`}
                        color={item.items_received === item.items_count ? "success" : "default"}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(item.status)}
                        color={getStatusColor(item.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getReceivingStatusLabel(item.receiving_status)}
                        color={getReceivingStatusColor(item.receiving_status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<VisibilityIcon />}
                          onClick={() => navigate(`/purchaseorders/details/${item.purchase_order_id}`)}
                        >
                          Ver
                        </Button>

                        <IconButton
                          aria-label="export"
                          color="primary"
                          onClick={() => exportExcel(item.purchase_order_id)}
                        >
                          <FileDownloadIcon />
                        </IconButton>

                        {(localStorage.getItem('role_id') === "1" || localStorage.getItem('role_id') === "2") &&
                          <IconButton
                            aria-label="delete"
                            color="primary"
                            onClick={() => handleDelete(item.purchase_order_id)}
                          >
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        }
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            style={{ marginTop: "20px", alignSelf: "center" }}
          />
        </Box>
      </div>

      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle="¿Está seguro que desea eliminar esta orden de compra?"
        send={deleteItem}
      />
    </div>
  );
}
